import {StyleSheet, Font} from "@react-pdf/renderer";

import Roboto from '../../../assets/fonts/Roboto/Roboto-Regular.ttf';
import RobotoItalic from '../../../assets/fonts/Roboto/Roboto-Italic.ttf';
import RobotoBold from '../../../assets/fonts/Roboto/Roboto-Bold.ttf';
import RobotoBoldItalic from '../../../assets/fonts/Roboto/Roboto-BoldItalic.ttf';

Font.register({
    family: 'Roboto',
    fonts: [
        {src: Roboto},
        {src: RobotoBold, fontWeight: 'bold'},
        {src: RobotoBold, fontWeight: '400'},
        {src: RobotoBold, fontWeight: '500'},
        {src: RobotoBold, fontWeight: '600'},
        {src: RobotoBold, fontWeight: '700'},
        {src: RobotoItalic, fontWeight: 'normal', fontStyle: 'italic'},
        {src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic'},
    ],
    format: 'truetype',
});

// ── Design tokens (mirror the reference Worksheet Lease HTML) ─────────
export const colors = {
    blue:           '#2171c7',
    blueLight:      '#f0f6ff',
    black:          '#000000',
    muted:          '#aaaaaa',
    chevron:        '#999999',
    border:         '#e0e0e0',  // panel outline
    borderLight:    '#f0f0f0',  // panel-body / col-header divider
    borderHairline: '#f5f5f5',  // data-row divider
    totalBorder:    '#d0d0d0',  // total-row stronger separator
    totalRowBg:     '#fafafa',
    footerBg:       '#fafafa',
    green:          '#2a8a2a',  // positive amounts
    white:          '#ffffff',
};

// All sizes shrunk modestly from the HTML reference so a full Dealer
// Worksheet (12 panels + chrome) fits inside a single A4 page without
// needing page breaks (the DealerDisclosurePDF passes `wrap={false}`).
export const baseStyles = StyleSheet.create({
    page: {
        padding: 0,
        fontFamily: 'Roboto',
        fontSize: 9,
        color: colors.black,
        backgroundColor: colors.white,
    },

    container: {
        flexDirection: 'column',
    },

    // ── Top bar ────────────────────────────────────────────────────────
    topbar: {
        flexDirection: 'column',
    },
    topbarInner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    topbarTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: colors.blue,
        letterSpacing: 0.3,
    },
    topbarSubtitle: {
        fontSize: 10,
        fontWeight: 600,
        color: colors.black,
        marginLeft: 'auto',
    },
    topbarBorder: {
        height: 1,
        backgroundColor: colors.border,
        marginHorizontal: 16,
    },

    // ── Vehicle info row (year + spec, then 5 key-value pills) ────────
    // Padding/margins tuned down from the first pass — the row was wider
    // than it needed to be and was crowding out the pill values. The pill
    // dividers now sit close to the text they separate, with consistent
    // 8pt of whitespace on either side instead of 14pt.
    vehicleInfoRow: {
        flexDirection: 'row',
        // Top-align the columns so the title ("2026 …") sits on the same
        // row as the pill labels (Lender / Type / …) and the trim/spec
        // wraps underneath rather than vertically centering the whole block.
        alignItems: 'flex-start',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderBottom: `1px solid ${colors.borderLight}`,
    },
    vehicleNameBlock: {
        // Cap the width so a long trim/spec wraps to a second line instead
        // of stretching the column and pushing the pills off-screen.
        maxWidth: 150,
        paddingRight: 10,
        marginRight: 10,
        borderRight: `1px solid ${colors.border}`,
    },
    vehicleName: {
        fontSize: 10,
        fontWeight: 700,
        color: colors.black,
    },
    vehicleSpec: {
        fontSize: 9,
        color: colors.black,
        marginTop: 2,
    },
    pillContainer: {
        flexDirection: 'row',
        // Top-align so every pill label sits on the same row even when the
        // Lender value wraps to two lines and makes that pill taller.
        alignItems: 'flex-start',
        flex: 1,
    },
    pillBlock: {
        marginRight: 8,
        flexShrink: 1,    // long values shrink before pushing neighbors
        minWidth: 0,      // lets the inner Text wrap rather than overflow
    },
    pillDivider: {
        width: 1,
        height: 22,
        backgroundColor: colors.border,
        marginRight: 8,
        flexShrink: 0,    // dividers never shrink
    },
    pillLabel: {
        fontSize: 7,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: colors.black,
    },
    pillValue: {
        fontSize: 10,    
        color: colors.black,
        marginTop: 1,
    },

    // ── Dealer-only lender row (below the vehicle info strip) ─────────
    viLenderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 6,
        borderBottom: `1px solid ${colors.borderLight}`,
    },
    viLenderLabel: {
        fontSize: 7,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: colors.black,
        marginRight: 8,
        flexShrink: 0,
    },
    viLenderValue: {
        fontSize: 10,
        color: colors.black,
    },

    // ── Main two-column layout ─────────────────────────────────────────
    mainGrid: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        paddingTop: 10,
        paddingBottom: 12,
    },
    column: {
        flexDirection: 'column',
        flex: 1,
    },
    columnLeft:  {marginRight: 6},
    columnRight: {marginLeft: 6},

    // ── Panel ──────────────────────────────────────────────────────────
    panel: {
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
        backgroundColor: colors.white,
        marginBottom: 8,
    },
    panelHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderBottom: `1px solid ${colors.borderLight}`,
    },
    panelTitle: {
        fontSize: 11,
        fontWeight: 700,
        color: colors.black,
    },
    panelBody: {
        paddingHorizontal: 12,
        paddingTop: 2,
        paddingBottom: 4,
    },

    // ── Data rows ──────────────────────────────────────────────────────
    // minHeight (not height) so labels that wrap to two lines aren't
    // clipped — the row grows to accommodate the wrapped text.
    dataRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 18,
        paddingTop: 2,
        paddingBottom: 2,
        borderBottom: `1px solid ${colors.borderHairline}`,
    },
    dataRowLast: {
        borderBottom: 0,
    },
    rowLabel: {
        fontSize: 10,
        color: colors.black,
        flex: 1,
        paddingRight: 6,    // leave breathing room before the value column
    },
    rowValue: {
        fontSize: 10,
        width: 90,          // widest value ($125,550.00) is ~55pt; the rest goes to the label
        flexShrink: 0,      // value column always takes its full width
        textAlign: 'right',
        color: colors.black,
        fontWeight: 400,
    },
    rowValueBold:  {fontWeight: 700},
    rowValueBlue:  {color: colors.blue, fontWeight: 700},
    rowValueGreen: {color: colors.green},
    rowValueMuted: {color: colors.black, fontWeight: 400},

    // ── Total row variant ──────────────────────────────────────────────
    // Stronger top border + bold label + bold value.
    totalRow: {
        borderTop: `1px solid ${colors.totalBorder}`,
    },
    totalRowLabel: {fontWeight: 700, color: colors.black},
    totalRowValue: {fontWeight: 700, color: colors.black},

    // ── Section (for tables of repeating "name | value" rows, like the
    //    Rebates and Fees breakdowns). Same look as a panel-body but with
    //    a uppercase muted column header above the rows. ───────────────
    sectionBody: {
        paddingHorizontal: 12,
        paddingTop: 2,
        paddingBottom: 4,
    },
    colHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 18,
        borderBottom: `1px solid ${colors.borderLight}`,
    },
    colHeaderCell: {
        fontSize: 8,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        color: colors.chevron,
    },

    // ── Footer ─────────────────────────────────────────────────────────
    pageFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.footerBg,
    },
    footerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    footerText: {
        fontSize: 9,
        color: colors.black,
        marginRight: 6,
    },
    footerLogo: {
        width: 90,
        objectFit: 'contain',
    },
    // The account's own logo, centered on its own row in the white space just
    // above the "Powered by" strip. Costs ~30pt of height — kept deliberately
    // tight because the Dealer worksheet packs 12 panels onto one A4 page with
    // wrap={false} and has the least slack of the four.
    footerCompanyRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 5,
    },
    footerCompanyLogo: {
        height: 20,
        maxWidth: 180,
        objectFit: 'contain',
    },
    footerStrong: {
        color: colors.blue,
        fontWeight: 600,
    },
});
